import * as R from 'ramda';

import { Question, RecordWithOrderAndIDProp, ValueOf } from './types';

/**
 * Takes a dict and returns the highest order prop value
 */
export const getHighestOrderPropValue = <T extends RecordWithOrderAndIDProp>(
  dict: T,
): number =>
  R.pipe(Object.values, R.map(R.prop('order')), R.reduce(R.max, 0))(dict);

/**
 * Turns dict into an array and sorts it by the order prop
 */
export const dictToArrayByOrder = <T extends RecordWithOrderAndIDProp>(
  dict: T,
): ValueOf<T>[] => R.pipe(Object.values, R.sortBy(R.prop('order')))(dict);

/**
 * Takes a dict, delletes it's member by given id, updates the order prop on each member
 */
export const deleteMemberOfDictAndUpdateOrderProp = <
  T extends RecordWithOrderAndIDProp
>(
  id: string,
  dict: T,
): T => {
  const setOrderBasedOnIndex = (val, index) => R.assoc('order', index + 1, val);
  const sortByOrder = R.sortBy(R.prop('order'));
  const updateOrderProp = R.addIndex(R.map)(setOrderBasedOnIndex);

  return R.pipe(
    R.dissoc(id),
    Object.values,
    sortByOrder,
    updateOrderProp,
    R.map(v => [v.id, v]),
    Object.fromEntries,
  )(dict);
};

/**
 * Takes a dict and moves its member by given id in the specified direction - up or down
 */
export const swapDictMembersByOrder = <T extends RecordWithOrderAndIDProp>(
  id: keyof T,
  dict: T,
  direction: 'up' | 'down',
): T => {
  const newDict = { ...dict };

  direction === 'up' ? (newDict[id].order -= 1) : (newDict[id].order += 1);
  Object.values(newDict).forEach(q => {
    if (newDict[id].order === q.order && q.id !== id) {
      if (direction === 'up') {
        newDict[q.id].order += 1;
      } else {
        newDict[q.id].order -= 1;
      }
    }
  });
  return newDict;
};

export const generateAttributeName = (qid: Question['id']) =>
  `pos_brazil_feedback_plugin_question_${qid}`;
